import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, doc, getDoc, updateDoc, addDoc, onSnapshot, limit } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { createBillFromBooking } from '../../utils/billGenerator';
import { getServicesListFromProfile } from '../../utils/salonServices';
import { getSalonStaffRoster } from '../../services/staffConfigService';
import { parseBookingTimeToMinutes } from '../../utils/bookingTime';
import BillView from '../../components/BillView';
import DailyReport from '../../components/DailyReport';
import './Dashboard.css';

// Service Status Constants
const SERVICE_STATUS = {
  NOT_STARTED: 'not_started',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed'
};

// Derive customer id from booking (mobile may use customerId, customer_id, userId, or uid)
const getBookingCustomerId = (booking) => {
  const id = booking?.customerId ?? booking?.customer_id ?? booking?.userId ?? booking?.uid;
  return id && !String(id).startsWith('walk-in') ? id : null;
};

// Mirrors the mobile app's isBookingTimeElapsed — true once "now" is strictly past
// the booking's scheduled slot. Safe client-side: the browser runs in the salon's
// local timezone, same as the stored booking date/time.
const isBookingTimeElapsed = (booking, now = new Date()) => {
  const bookingDateTime = new Date(booking.bookingDate);
  const minutes = parseBookingTimeToMinutes(booking.bookingTime);
  bookingDateTime.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);
  return now.getTime() > bookingDateTime.getTime();
};

// Reschedule request options + message — mirror RESCHEDULE_PREFERRED_TIME_LABELS
// and RESCHEDULE_REQUEST_MESSAGE in the mobile app's bookingService.js so the
// customer app renders the request identically wherever it was sent from.
const RESCHEDULE_PREFERRED_TIME_LABELS = {
  tomorrow: 'Tomorrow',
  this_week: 'This Week',
  this_weekend: 'This Weekend',
  next_week: 'Next Week',
  anytime: 'Anytime',
};

const RESCHEDULE_REQUEST_MESSAGE =
  "Due to unforeseen circumstances, we're unable to provide your service at the scheduled time. " +
  'We apologize for the inconvenience. Please reschedule your appointment';

// Active = not completed, cancelled, or no_show (used for reward badge logic only; dashboard displays all)
const isActiveBooking = (data) => {
  const status = (data?.bookingStatus ?? '').toLowerCase();
  const serviceStatus = (data?.serviceStatus ?? '').toLowerCase();
  if (status === 'completed' || serviceStatus === 'completed') return false;
  if (status?.includes('cancelled')) return false;
  if (status === 'no_show') return false;
  return true;
};

const formatDate = (isoString) => {
  const date = new Date(isoString);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const bookingDay = new Date(date);
  bookingDay.setHours(0, 0, 0, 0);
  
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  
  if (bookingDay.getTime() === today.getTime()) {
    return 'Today';
  } else if (bookingDay.getTime() === tomorrow.getTime()) {
    return 'Tomorrow';
  } else {
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
    // Output: "Thu, Jan 9"
  }
};

function numOrNull(v) {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function formatRupees(n) {
  if (n == null || !Number.isFinite(n)) return '—';
  const x = Number(n);
  if (Math.abs(x - Math.round(x)) < 1e-9) return String(Math.round(x));
  return x.toFixed(2);
}

/**
 * GST fields written on the booking at booking time (see mobile app GST toggle).
 * Older bookings won't have these fields — treat gstEnabled as falsy and fall back
 * to totalPrice with no GST row, matching pre-GST behavior.
 */
function getGstInfo(booking) {
  const enabled = !!booking?.gstEnabled;
  if (!enabled) {
    return { enabled: false, percentage: 0, amount: 0 };
  }
  return {
    enabled: true,
    percentage: numOrNull(booking.gstPercentage) ?? 0,
    amount: numOrNull(booking.gstAmount) ?? 0
  };
}

/**
 * Ulloor custom offer fields (mobile / Firestore may use camelCase or snake_case).
 */
function getUlloorCustomOfferInfo(booking) {
  if (!booking || typeof booking !== 'object') {
    return {
      applied: false,
      pct: null,
      discountAmount: null,
      isDateOverride: false,
      subtotalBeforePlatformOffers: null
    };
  }
  const applied =
    booking.ulloorCustomOfferApplied === true ||
    booking.ulloor_custom_offer_applied === true;
  const pctRaw =
    booking.ulloorCustomOfferPercentage ?? booking.ulloor_custom_offer_percentage;
  const discountRaw =
    booking.ulloorCustomOfferDiscountAmount ?? booking.ulloor_custom_offer_discount_amount;
  const isDateOverride =
    booking.ulloorCustomOfferIsDateOverride === true ||
    booking.ulloor_custom_offer_is_date_override === true;
  const subRaw =
    booking.subtotalBeforePlatformOffers ?? booking.subtotal_before_platform_offers;

  const pct = pctRaw != null && pctRaw !== '' ? Number(pctRaw) : NaN;
  const discountAmount = discountRaw != null && discountRaw !== '' ? Number(discountRaw) : NaN;
  const subtotal = subRaw != null && subRaw !== '' ? Number(subRaw) : NaN;

  return {
    applied,
    pct: Number.isFinite(pct) ? pct : null,
    discountAmount: Number.isFinite(discountAmount) ? discountAmount : null,
    isDateOverride,
    subtotalBeforePlatformOffers: Number.isFinite(subtotal) ? subtotal : null
  };
}

/**
 * Receipt-style lines: Service → Salon offer → Additional (Ulloor Styles) → To pay (matches mobile).
 */
function buildPricingBreakdown(booking, info, gst) {
  const serviceAmount =
    numOrNull(booking.originalPrice ?? booking.original_price) ??
    numOrNull(booking.servicePriceBeforeSalon ?? booking.service_price_before_salon) ??
    numOrNull(booking.servicePrice ?? booking.service_price);

  const subtotal = info.subtotalBeforePlatformOffers;

  let salonDiscount = numOrNull(
    booking.salonDiscountAmount ??
      booking.salon_discount_amount ??
      booking.salonOfferDiscountAmount ??
      booking.salon_offer_discount_amount
  );
  if (salonDiscount != null) {
    salonDiscount = Math.abs(salonDiscount);
  } else if (serviceAmount != null && subtotal != null && serviceAmount > subtotal) {
    salonDiscount = serviceAmount - subtotal;
  }

  const toPay =
    numOrNull(booking.totalPrice ?? booking.total_price) ??
    numOrNull(booking.servicePrice ?? booking.service_price);

  const payableWithGst = gst.enabled ? (numOrNull(booking.grandTotal) ?? toPay) : null;

  return {
    serviceAmount,
    salonDiscount: salonDiscount != null && salonDiscount > 0 ? salonDiscount : null,
    pct: info.pct,
    ulloorSave: info.discountAmount,
    toPay,
    payableWithGst
  };
}

/**
 * Advance Pay chips (₹11 Razorpay advance) — mirrors the mobile app's booking-card
 * badges. Fields (advancePaid/advanceAmount/remainingAmount) are written by Cloud
 * Functions on the booking document.
 */
function AdvancePaymentBadge({ booking }) {
  if (booking?.advancePaid !== true) return null;
  const advanceAmount = booking.advanceAmount || 11;
  const remaining = Math.max(numOrNull(booking.remainingAmount) ?? 0, 0);
  const status = String(booking.bookingStatus || '');
  const isCancelled = status.includes('cancelled') || status === 'no_show';
  const balanceSettled = status === 'completed' || booking.paymentStatus === 'received';
  const isInProgress = booking.serviceStatus === SERVICE_STATUS.IN_PROGRESS;

  if (isCancelled) {
    return (
      <div className="advance-badge-row">
        <span className="advance-chip muted">Advance ₹{advanceAmount} paid (non-refundable)</span>
      </div>
    );
  }
  return (
    <div className="advance-badge-row">
      <span className="advance-chip paid">✓ Advance ₹{advanceAmount} paid</span>
      {!balanceSettled && (
        <span className="advance-chip remaining">
          {isInProgress ? `Collect now: ₹${remaining}` : `Collect: ₹${remaining}`}
        </span>
      )}
    </div>
  );
}

/**
 * Single price row when no Ulloor custom offer; full breakdown when applied (aligned with mobile).
 */
function BookingCardPriceBlock({ booking }) {
  const info = getUlloorCustomOfferInfo(booking);
  const gst = getGstInfo(booking);
  const hasSimplePrice = !!(booking.totalPrice || booking.servicePrice);

  if (!info.applied) {
    if (!hasSimplePrice) return null;
    const preTaxAmount = booking.totalPrice || booking.servicePrice;
    const payableWithGst = gst.enabled ? (numOrNull(booking.grandTotal) ?? preTaxAmount) : null;
    return (
      <div className="service-price-block">
        <div className="service-price">
          <span className="icon">💰</span>
          <strong>₹{formatRupees(preTaxAmount)}</strong>
        </div>
        {gst.enabled && (
          <div className="service-price-gst">
            <span className="service-price-gst-label">With GST ({formatRupees(gst.percentage)}%)</span>
            <span className="service-price-gst-value">₹{formatRupees(payableWithGst)}</span>
          </div>
        )}
      </div>
    );
  }

  const b = buildPricingBreakdown(booking, info, gst);
  const hasUlloorLine = b.pct != null || b.ulloorSave != null;
  const hasAnyBreakdownRow =
    b.serviceAmount != null ||
    b.salonDiscount != null ||
    hasUlloorLine ||
    b.toPay != null;
  if (!hasAnyBreakdownRow) return null;

  const hasStripContent =
    b.serviceAmount != null || b.salonDiscount != null || hasUlloorLine;

  return (
    <div className="booking-price-breakdown" role="region" aria-label="Price breakdown">
      {hasStripContent && (
        <div className="price-breakdown-strip">
          {b.serviceAmount != null && (
            <div className="price-breakdown-cell price-breakdown-cell--service">
              <span className="price-breakdown-label">Service</span>
              <span className="price-breakdown-value">₹{formatRupees(b.serviceAmount)}</span>
            </div>
          )}
          {b.salonDiscount != null && (
            <div className="price-breakdown-cell price-breakdown-cell--deduction">
              <span className="price-breakdown-label">Salon offer</span>
              <span className="price-breakdown-value price-breakdown-minus">−₹{formatRupees(b.salonDiscount)}</span>
            </div>
          )}
          {hasUlloorLine && (
            <div className="price-breakdown-cell price-breakdown-cell--ulloor">
              <span className="price-breakdown-label">Additional (Ulloor Styles)</span>
              <span className="price-breakdown-value price-breakdown-ulloor-value">
                <span className="price-breakdown-ulloor-inline">
                  {b.pct != null && <span className="price-breakdown-ulloor-pct">{b.pct}%</span>}
                  {b.pct != null && b.ulloorSave != null && ' '}
                  {b.ulloorSave != null && (
                    <span className="price-breakdown-ulloor-brackets">
                      (<span className="price-breakdown-minus">−₹{formatRupees(b.ulloorSave)}</span>)
                    </span>
                  )}
                </span>
              </span>
            </div>
          )}
        </div>
      )}
      {b.toPay != null && (
        <div className="price-breakdown-topay">
          <span className="price-breakdown-label">Final amount to pay</span>
          <span className="price-breakdown-value">₹{formatRupees(b.toPay)}</span>
        </div>
      )}
      {gst.enabled && b.payableWithGst != null && (
        <div className="price-breakdown-topay price-breakdown-topay--gst">
          <span className="price-breakdown-label">With GST ({formatRupees(gst.percentage)}%)</span>
          <span className="price-breakdown-value">₹{formatRupees(b.payableWithGst)}</span>
        </div>
      )}
    </div>
  );
}

function Dashboard() {
  const { salonData } = useAuth();
  const navigate = useNavigate();
  const [todayBookings, setTodayBookings] = useState([]);
  const [walkInBookings, setWalkInBookings] = useState([]);
  const [upcomingBookings, setUpcomingBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Reschedule request states (salon asks the customer to reschedule)
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [bookingForReschedule, setBookingForReschedule] = useState(null);
  const [selectedPreferredTime, setSelectedPreferredTime] = useState('');
  
  // Walk-in customer states (multi-service: from list + custom entries)
  const [showWalkInModal, setShowWalkInModal] = useState(false);
  const [walkInCustomerName, setWalkInCustomerName] = useState('');
  const [walkInCustomerPhone, setWalkInCustomerPhone] = useState('');
  const [walkInSelectedFromList, setWalkInSelectedFromList] = useState([]); // [{ name, price }]
  const [walkInCustomEntries, setWalkInCustomEntries] = useState([]); // [{ name, price }]
  const [walkInTotalOverride, setWalkInTotalOverride] = useState(null); // null = use computed total; number = editable override
  const [walkInStaffId, setWalkInStaffId] = useState('');
  const [walkInLoading, setWalkInLoading] = useState(false);

  // Edit walk-in states (multi-service, same as add)
  const [showEditWalkInModal, setShowEditWalkInModal] = useState(false);
  const [editingWalkIn, setEditingWalkIn] = useState(null);
  const [editWalkInName, setEditWalkInName] = useState('');
  const [editWalkInPhone, setEditWalkInPhone] = useState('');
  const [editWalkInSelectedFromList, setEditWalkInSelectedFromList] = useState([]);
  const [editWalkInCustomEntries, setEditWalkInCustomEntries] = useState([]);
  const [editWalkInTotalOverride, setEditWalkInTotalOverride] = useState(null);
  const [editWalkInLoading, setEditWalkInLoading] = useState(false);

  // Staff assignment states
  const [staff, setStaff] = useState([]);
  const [staffMap, setStaffMap] = useState({});
  const [showStaffModal, setShowStaffModal] = useState(false);
  const [staffLoading, setStaffLoading] = useState(false);
  
  // Payment mode modal states
  const [showPaymentModeModal, setShowPaymentModeModal] = useState(false);
  const [selectedPaymentMode, setSelectedPaymentMode] = useState('');
  const [bookingForCompletion, setBookingForCompletion] = useState(null);
  const [completionStaffId, setCompletionStaffId] = useState(null);
  
  // Bill view states
  const [showBillView, setShowBillView] = useState(false);
  const [selectedBookingForBill, setSelectedBookingForBill] = useState(null);
  
  // Daily report states
  const [showDailyReport, setShowDailyReport] = useState(false);
  
  // Reward progress: which customer has reward eligible + summary; which booking id shows the badge per customer
  const [rewardProgressByCustomer, setRewardProgressByCustomer] = useState({});
  const [activeRewardBookingIdByCustomer, setActiveRewardBookingIdByCustomer] = useState({});
  
  // Walk-in payment mode
  const [walkInPaymentMode, setWalkInPaymentMode] = useState('cash');

  const walkInServiceList = getServicesListFromProfile(salonData?.profile || {});

  // Phone search for walk-in: list of { phone, name } from previous bookings
  const [phoneSearchResults, setPhoneSearchResults] = useState([]);
  const [phoneSearchLoading, setPhoneSearchLoading] = useState(false);
  const phoneSearchDebounceRef = useRef(null);
  const phoneListRef = useRef(null);
  
  // Refs for walk-in form inputs (order: phone, name, ...)
  const phoneInputRef = useRef(null);
  const nameInputRef = useRef(null);

  const fetchStaff = useCallback(() => {
    const roster = getSalonStaffRoster(salonData);
    const mapping = {};
    roster.forEach(staffMember => {
      mapping[staffMember.id] = staffMember.name;
    });
    setStaffMap(mapping);
    setStaff(roster);
  }, [salonData]);

  useEffect(() => {
    if (salonData?.salonId) {
      fetchStaff();

      // Set up real-time listener for bookings
      const bookingsQuery = query(
        collection(db, 'bookings'),
        where('salonId', '==', salonData.salonId)
      );

      const unsubscribe = onSnapshot(bookingsQuery, (querySnapshot) => {
        // Get today's date range (matching mobile app)
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const todayBookingsData = [];
        const walkInBookingsData = [];
        const upcomingBookingsData = [];

        querySnapshot.forEach((docSnapshot) => {
          const data = docSnapshot.data();

          // Advance Pay payment holds (payment_pending/payment_expired and any future
          // payment_* status) are Cloud-Function-managed and never shown — same
          // filtering the mobile app applies (isPaymentHoldBooking).
          if (String(data.bookingStatus || '').startsWith('payment_')) return;

          const bookingDateISO = data.bookingDate;

          const bookingDate = new Date(bookingDateISO);
          const bookingDay = new Date(bookingDate);
          bookingDay.setHours(0, 0, 0, 0);

          const booking = {
            ...data,
            id: docSnapshot.id
          };

          if (bookingDay.getTime() === today.getTime()) {
            if (booking.isWalkIn) {
              walkInBookingsData.push(booking);
            } else {
              todayBookingsData.push(booking);
            }
          } else if (bookingDay > today) {
            // Unlike Today's Bookings (which intentionally keeps cancelled/no-show entries
            // visible with a badge, as a record of the day), a cancelled future booking has
            // nothing left to action — leave it out of Upcoming entirely.
            if (isActiveBooking(booking)) {
              upcomingBookingsData.push(booking);
            }
          }
        });

        // Sort by actual clock time (string sort breaks: "10:00 PM" < "8:00 PM" lexicographically)
        const byBookingTime = (a, b) =>
          parseBookingTimeToMinutes(a.bookingTime) - parseBookingTimeToMinutes(b.bookingTime);

        todayBookingsData.sort(byBookingTime);
        walkInBookingsData.sort(byBookingTime);

        upcomingBookingsData.sort((a, b) => {
          const dateCompare = new Date(a.bookingDate) - new Date(b.bookingDate);
          if (dateCompare !== 0) return dateCompare;
          return byBookingTime(a, b);
        });

        setTodayBookings(todayBookingsData);
        setWalkInBookings(walkInBookingsData);
        setUpcomingBookings(upcomingBookingsData);
        setLoading(false);
      }, (error) => {
        setLoading(false);
      });

      // Cleanup listener on unmount
      return () => unsubscribe();
    } else if (salonData && !salonData.salonId) {
      setLoading(false);
    }
  }, [salonData, fetchStaff]);

  // Active-only lists for reward logic (badge only on active bookings; display shows all)
  const activeTodayBookings = useMemo(() => todayBookings.filter((b) => isActiveBooking(b)), [todayBookings]);
  const activeUpcomingBookings = useMemo(() => upcomingBookings.filter((b) => isActiveBooking(b)), [upcomingBookings]);

  // Today's confirmed bookings whose booked slot time has already passed — the salon
  // still needs to act on them (start/complete/cancel). Mirrors the mobile
  // dashboard's pending-action banner.
  const pendingActionCount = useMemo(
    () => todayBookings.filter(
      (b) => String(b.bookingStatus || '').toLowerCase() === 'confirmed' &&
        b.serviceStatus !== SERVICE_STATUS.IN_PROGRESS &&
        isBookingTimeElapsed(b)
    ).length,
    [todayBookings]
  );

  // Reward progress: fetch per customer and compute which booking shows "Reward applicable" badge (active items only)
  useEffect(() => {
    const salonId = salonData?.salonId;
    if (!salonId) return;

    const combinedForReward = [...activeTodayBookings, ...activeUpcomingBookings];
    const customerIds = [...new Set(
      combinedForReward
        .map((b) => getBookingCustomerId(b))
        .filter(Boolean)
    )];

    if (customerIds.length === 0) {
      setRewardProgressByCustomer({});
      setActiveRewardBookingIdByCustomer({});
      return;
    }

    let cancelled = false;

    (async () => {
      const progress = {};
      await Promise.all(
        customerIds.map(async (customerId) => {
          if (cancelled) return;
          try {
            const ref = doc(db, 'customers', customerId, 'rewardProgress', salonId);
            const snap = await getDoc(ref);
            const data = snap.exists() ? snap.data() : {};
            // Support both camelCase and snake_case (Firestore / mobile may use either)
            const rewardEligible = data.rewardEligible === true || data.reward_eligible === true;
            let rewardSummary = (data.rewardSummary ?? data.reward_summary ?? '').trim();
            if (!rewardSummary && rewardEligible) {
              const consecutive = data.consecutiveMonthsCount ?? data.consecutive_months_count;
              const required = data.requiredMonths ?? data.required_months;
              if (typeof consecutive === 'number' && typeof required === 'number') {
                rewardSummary = `${consecutive}/${required} consecutive months completed`;
              }
            }
            progress[customerId] = { rewardEligible, rewardSummary };
          } catch (err) {
            progress[customerId] = { rewardEligible: false, rewardSummary: '' };
          }
        })
      );

      if (cancelled) return;
      setRewardProgressByCustomer(progress);

      // Sort combined by date ascending, then time (12h AM/PM) ascending — match mobile for "first" booking per customer
      const sortedForActive = [...combinedForReward].sort((a, b) => {
        const tA = new Date(a.bookingDate).getTime();
        const tB = new Date(b.bookingDate).getTime();
        if (tA !== tB) return tA - tB;
        return parseBookingTimeToMinutes(a.bookingTime) - parseBookingTimeToMinutes(b.bookingTime);
      });

      const active = {};
      for (const b of sortedForActive) {
        const cid = getBookingCustomerId(b);
        if (!cid) continue;
        const rewardApplied = b.rewardApplied === true || b.reward_applied === true;
        if (rewardApplied) continue;
        if (!progress[cid]?.rewardEligible) continue;
        if (active[cid] != null) continue;
        active[cid] = b.id;
      }
      setActiveRewardBookingIdByCustomer(active);
    })();

    return () => { cancelled = true; };
  }, [salonData?.salonId, activeTodayBookings, activeUpcomingBookings]);

  // No longer needed - data updates in real-time via onSnapshot listener
  // Keeping for compatibility with existing UI (refresh button)
  const fetchDashboardData = () => {
    // Data is already being updated in real-time via the onSnapshot listener
    console.log('✨ Dashboard is auto-updating in real-time!');
  };

  // Handle Start Service
  const handleStartService = async (booking) => {
    const confirmed = window.confirm(
      `Start service for ${booking.customerName}?\n\nService: ${booking.serviceName}\nTime: ${booking.bookingTime}`
    );
    
    if (!confirmed) return;

    try {
      setActionLoading(true);
      const bookingRef = doc(db, 'bookings', booking.id);
      await updateDoc(bookingRef, {
        serviceStatus: SERVICE_STATUS.IN_PROGRESS,
        serviceStartedAt: new Date()
      });
      // Real-time listener will auto-update the UI
    } catch (error) {
      alert('Failed to start service. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle Complete Service - Show payment mode modal
  const handleCompleteService = async (booking) => {
    setBookingForCompletion(booking);
    setSelectedPaymentMode('');
    setCompletionStaffId(booking.staffId || null);
    setShowPaymentModeModal(true);
  };

  // Close payment mode modal
  const handleClosePaymentModeModal = () => {
    setShowPaymentModeModal(false);
    setBookingForCompletion(null);
    setSelectedPaymentMode('');
    setCompletionStaffId(null);
  };
  
  // Confirm completion with payment mode
  const handleConfirmCompletion = async () => {
    if (!selectedPaymentMode) {
      alert('Please select a payment mode');
      return;
    }
    if (!completionStaffId) {
      alert('Please assign staff who performed the service');
      return;
    }

    try {
      setActionLoading(true);

      // Update booking status (staff is mandatory)
      const bookingRef = doc(db, 'bookings', bookingForCompletion.id);
      await updateDoc(bookingRef, {
        serviceStatus: SERVICE_STATUS.COMPLETED,
        serviceCompletedAt: new Date(),
        bookingStatus: 'completed',
        paymentMode: selectedPaymentMode,
        staffId: completionStaffId
      });

      // Generate bill
      const updatedBooking = {
        ...bookingForCompletion,
        serviceStatus: SERVICE_STATUS.COMPLETED,
        bookingStatus: 'completed',
        paymentMode: selectedPaymentMode,
        staffId: completionStaffId
      };
      
      await createBillFromBooking(updatedBooking, salonData, selectedPaymentMode);
      
      alert('Service completed and bill generated successfully!');
      handleClosePaymentModeModal();
      // Real-time listener will auto-update the UI
    } catch (error) {
      alert('Failed to complete service. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };
  
  // Handle View Bill
  const handleViewBill = (booking) => {
    setSelectedBookingForBill(booking.id);
    setShowBillView(true);
  };
  
  // Close Bill View
  const handleCloseBillView = () => {
    setShowBillView(false);
    setSelectedBookingForBill(null);
  };

  // Handle Daily Report
  const handleOpenDailyReport = () => {
    setShowDailyReport(true);
  };

  const handleCloseDailyReport = () => {
    setShowDailyReport(false);
  };

  // Helper function to get staff name by ID
  const getStaffName = (staffId) => {
    return staffMap[staffId] || `Staff ID: ${staffId}`;
  };

  // Handle Send Thank You Message
  const handleSendThankYou = async (booking) => {
    // Format phone number for WhatsApp (remove spaces, +, and ensure it starts with country code)
    let phoneNumber = booking.customerPhone.replace(/\s+/g, '').replace(/\+/g, '');
    
    // If phone number doesn't start with country code, add 91 (India)
    if (!phoneNumber.startsWith('91') && phoneNumber.length === 10) {
      phoneNumber = '91' + phoneNumber;
    }

    // Create thank you message with specified format
    const salonName = salonData?.profile?.shopName || 'Ulloor Styles';
    const message = 
      `Hi ${booking.customerName}!,\n\n` +
      `Thank you for visiting ${salonName}!\n` +
      `We hope you enjoyed your service.\n\n` +
      `We look forward to serving you again!\n\n` +
      `Regards,\n` +
      `${salonName}`;

    // Open WhatsApp Web with pre-filled message
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');

    // Record that thank you message was sent
    try {
      const bookingRef = doc(db, 'bookings', booking.id);
      await updateDoc(bookingRef, {
        thankYouMessageSent: true,
        thankYouMessageSentAt: new Date()
      });
      // Real-time listener will auto-update the UI
    } catch (error) {
      // Don't show error to user as the message was already opened
    }
  };

  // Open cancel modal
  const handleOpenCancelModal = (booking) => {
    setSelectedBooking(booking);
    setCancelReason('');
    setShowCancelModal(true);
  };

  // Close cancel modal
  const handleCloseCancelModal = () => {
    setShowCancelModal(false);
    setSelectedBooking(null);
    setCancelReason('');
  };

  // Reschedule request: a nudge, not an execution — the customer's slot stays held
  // until they actually pick a new time. Mirrors the mobile app's
  // requestBookingReschedule field-for-field.
  const handleOpenRescheduleModal = (booking) => {
    if (booking.rescheduleRequest?.status === 'pending') {
      alert('A reschedule request is already pending for this appointment.');
      return;
    }
    setBookingForReschedule(booking);
    setSelectedPreferredTime('');
    setShowRescheduleModal(true);
  };

  const handleCloseRescheduleModal = () => {
    setShowRescheduleModal(false);
    setBookingForReschedule(null);
    setSelectedPreferredTime('');
  };

  const handleConfirmRescheduleRequest = async () => {
    if (!selectedPreferredTime) {
      alert('Please select a preferred time window for the customer.');
      return;
    }
    try {
      setActionLoading(true);
      const bookingRef = doc(db, 'bookings', bookingForReschedule.id);
      await updateDoc(bookingRef, {
        rescheduleRequest: {
          status: 'pending',
          message: RESCHEDULE_REQUEST_MESSAGE,
          preferredTime: selectedPreferredTime,
          requestedAt: new Date().toISOString(),
          requestedBy: salonData.salonId,
          fulfilledAt: null,
          dismissedAt: null,
        },
        updatedAt: new Date().toISOString()
      });
      alert('The customer has been asked to reschedule their appointment.');
      handleCloseRescheduleModal();
      // Real-time listener will auto-update the UI
    } catch (error) {
      alert('Failed to send reschedule request. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle Cancel Booking
  const handleCancelBooking = async () => {
    if (!cancelReason.trim()) {
      alert('Please select or enter a cancellation reason');
      return;
    }

    try {
      setActionLoading(true);
      const bookingRef = doc(db, 'bookings', selectedBooking.id);
      await updateDoc(bookingRef, {
        bookingStatus: 'cancelled_by_salon',
        cancellationReason: cancelReason,
        cancelledAt: new Date()
      });
      handleCloseCancelModal();
      // Real-time listener will auto-update the UI
    } catch (error) {
      alert('Failed to cancel booking. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  // Walk-in customer functions
  const handleOpenWalkInModal = () => {
    setWalkInCustomerName('');
    setWalkInCustomerPhone('');
    setWalkInSelectedFromList([]);
    setWalkInCustomEntries([]);
    setWalkInTotalOverride(null);
    setWalkInStaffId('');
    setShowWalkInModal(true);
  };

  const handleCloseWalkInModal = () => {
    setShowWalkInModal(false);
    setWalkInCustomerName('');
    setWalkInCustomerPhone('');
    setWalkInSelectedFromList([]);
    setWalkInCustomEntries([]);
    setWalkInTotalOverride(null);
    setWalkInStaffId('');
    setPhoneSearchResults([]);
    setWalkInPaymentMode('cash');
  };

  // Combined walk-in services and total (from list + custom)
  const walkInCombinedServices = useMemo(() => {
    const fromList = walkInSelectedFromList.filter((s) => (s.name || '').trim());
    const custom = walkInCustomEntries.filter((s) => (s.name || '').trim());
    return [...fromList, ...custom];
  }, [walkInSelectedFromList, walkInCustomEntries]);
  const walkInTotalAmount = useMemo(
    () => walkInCombinedServices.reduce((sum, s) => sum + (Number(s.price) || 0), 0),
    [walkInCombinedServices]
  );
  const walkInEffectiveTotal = walkInTotalOverride !== null ? walkInTotalOverride : walkInTotalAmount;

  // When services change, clear override so total input shows the new computed sum
  useEffect(() => {
    if (showWalkInModal) setWalkInTotalOverride(null);
  }, [walkInSelectedFromList.length, walkInCustomEntries.length, showWalkInModal]);

  const toggleWalkInServiceFromList = (item) => {
    const key = `${item.name}-${item.price}`;
    setWalkInSelectedFromList((prev) => {
      const exists = prev.some((s) => `${s.name}-${s.price}` === key);
      if (exists) return prev.filter((s) => `${s.name}-${s.price}` !== key);
      return [...prev, { name: item.name, price: item.price }];
    });
  };

  const addWalkInCustomEntry = () => {
    setWalkInCustomEntries((prev) => [...prev, { name: '', price: 0 }]);
  };

  const updateWalkInCustomEntry = (index, field, value) => {
    setWalkInCustomEntries((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: field === 'price' ? (parseFloat(value) || 0) : value };
      return next;
    });
  };

  const removeWalkInCustomEntry = (index) => {
    setWalkInCustomEntries((prev) => prev.filter((_, i) => i !== index));
  };

  // Edit walk-in: combined services and total (same pattern as add)
  const editWalkInCombinedServices = useMemo(() => {
    const fromList = editWalkInSelectedFromList.filter((s) => (s.name || '').trim());
    const custom = editWalkInCustomEntries.filter((s) => (s.name || '').trim());
    return [...fromList, ...custom];
  }, [editWalkInSelectedFromList, editWalkInCustomEntries]);
  const editWalkInTotalAmount = useMemo(
    () => editWalkInCombinedServices.reduce((sum, s) => sum + (Number(s.price) || 0), 0),
    [editWalkInCombinedServices]
  );
  const editWalkInEffectiveTotal = editWalkInTotalOverride !== null ? editWalkInTotalOverride : editWalkInTotalAmount;

  const toggleEditWalkInServiceFromList = (item) => {
    const key = `${item.name}-${item.price}`;
    setEditWalkInSelectedFromList((prev) => {
      const exists = prev.some((s) => `${s.name}-${s.price}` === key);
      if (exists) return prev.filter((s) => `${s.name}-${s.price}` !== key);
      return [...prev, { name: item.name, price: item.price }];
    });
  };

  const addEditWalkInCustomEntry = () => {
    setEditWalkInCustomEntries((prev) => [...prev, { name: '', price: 0 }]);
  };

  const updateEditWalkInCustomEntry = (index, field, value) => {
    setEditWalkInCustomEntries((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: field === 'price' ? (parseFloat(value) || 0) : value };
      return next;
    });
  };

  const removeEditWalkInCustomEntry = (index) => {
    setEditWalkInCustomEntries((prev) => prev.filter((_, i) => i !== index));
  };

  const searchCustomersByPhone = React.useCallback(async (digits) => {
    if (!salonData?.salonId || digits.length < 3) {
      setPhoneSearchResults([]);
      return;
    }
    setPhoneSearchLoading(true);
    try {
      const q = query(
        collection(db, 'bookings'),
        where('salonId', '==', salonData.salonId),
        limit(300)
      );
      const snapshot = await getDocs(q);
      const seen = new Set();
      const list = [];
      const norm = (p) => (p || '').replace(/\D/g, '');
      snapshot.forEach((docSnap) => {
        const d = docSnap.data();
        const phone = (d.customerPhone || '').trim();
        if (!phone || phone === 'Not Provided') return;
        const phoneNorm = norm(phone);
        if (!phoneNorm.includes(digits)) return;
        const key = `${phoneNorm}-${(d.customerName || '').trim()}`;
        if (seen.has(key)) return;
        seen.add(key);
        list.push({ phone, name: (d.customerName || '').trim() || '—' });
      });
      setPhoneSearchResults(list.slice(0, 10));
    } catch (err) {
      setPhoneSearchResults([]);
    } finally {
      setPhoneSearchLoading(false);
    }
  }, [salonData?.salonId]);

  const handleWalkInPhoneChange = (value) => {
    const digits = value.replace(/\D/g, '');
    setWalkInCustomerPhone(digits);
    if (phoneSearchDebounceRef.current) clearTimeout(phoneSearchDebounceRef.current);
    if (digits.length < 3) {
      setPhoneSearchResults([]);
      return;
    }
    phoneSearchDebounceRef.current = setTimeout(() => {
      searchCustomersByPhone(digits);
    }, 350);
  };

  const handleSelectPhoneMatch = (item) => {
    setWalkInCustomerPhone(item.phone.replace(/\D/g, ''));
    setWalkInCustomerName(item.name !== '—' ? item.name : '');
    setPhoneSearchResults([]);
    nameInputRef.current?.focus();
  };

  useEffect(() => {
    if (!showWalkInModal || phoneSearchResults.length === 0) return;
    const handleClickOutside = (e) => {
      if (
        phoneInputRef.current?.contains(e.target) ||
        phoneListRef.current?.contains(e.target)
      ) return;
      setPhoneSearchResults([]);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showWalkInModal, phoneSearchResults.length]);

  const handleOpenEditWalkInModal = (booking) => {
    setEditingWalkIn(booking);
    setEditWalkInName(booking.customerName || '');
    setEditWalkInPhone(booking.customerPhone && booking.customerPhone !== 'Not Provided' ? booking.customerPhone : '');
    setEditWalkInTotalOverride(null);
    const combos = booking.comboServices && Array.isArray(booking.comboServices) && booking.comboServices.length > 0
      ? booking.comboServices
      : null;
    if (combos) {
      const fromList = [];
      const custom = [];
      combos.forEach((s) => {
        const name = (s.name || s).toString().trim();
        const price = Number(s.price) || 0;
        const inList = walkInServiceList.some((w) => w.name === name && Number(w.price) === price);
        if (inList) fromList.push({ name, price });
        else custom.push({ name, price });
      });
      setEditWalkInSelectedFromList(fromList);
      setEditWalkInCustomEntries(custom);
    } else {
      const sn = (booking.serviceName || '').trim();
      const amt = Number(booking.servicePrice ?? booking.totalPrice) || 0;
      setEditWalkInSelectedFromList([]);
      setEditWalkInCustomEntries(sn ? [{ name: sn, price: amt }] : []);
    }
    setShowEditWalkInModal(true);
  };

  const handleCloseEditWalkInModal = () => {
    setShowEditWalkInModal(false);
    setEditingWalkIn(null);
    setEditWalkInName('');
    setEditWalkInPhone('');
    setEditWalkInSelectedFromList([]);
    setEditWalkInCustomEntries([]);
    setEditWalkInTotalOverride(null);
  };

  const handleSaveEditWalkIn = async () => {
    if (!editingWalkIn?.id) return;
    if (!editWalkInName.trim()) {
      alert('Please enter customer name');
      return;
    }
    const phoneDigits = (editWalkInPhone || '').replace(/\D/g, '');
    if (phoneDigits.length < 10) {
      alert('Please enter a valid 10-digit phone number');
      return;
    }
    const combined = editWalkInCombinedServices.filter((s) => (s.name || '').trim());
    if (combined.length === 0) {
      alert('Please select at least one service or add a custom service');
      return;
    }
    const totalAmount = editWalkInEffectiveTotal;
    if (Number.isNaN(totalAmount) || totalAmount < 0) {
      alert('Please enter a valid total amount (0 or more)');
      return;
    }
    try {
      setEditWalkInLoading(true);
      const serviceNameDisplay = combined.map((s) => s.name.trim()).join(', ');
      const comboServices = combined.map((s) => ({ name: s.name.trim(), price: Number(s.price) || 0 }));
      const servicesSum = comboServices.reduce((sum, s) => sum + s.price, 0);
      const offerApplied = totalAmount < servicesSum ? totalAmount : undefined;
      const bookingRef = doc(db, 'bookings', editingWalkIn.id);
      await updateDoc(bookingRef, {
        customerName: editWalkInName.trim(),
        customerPhone: (editWalkInPhone || '').trim(),
        serviceName: serviceNameDisplay,
        servicePrice: comboServices[0]?.price ?? totalAmount,
        totalPrice: totalAmount,
        ...(offerApplied != null && { offerApplied }),
        isCombo: comboServices.length > 0,
        comboServices,
        updatedAt: new Date().toISOString()
      });
      alert('Walk-in updated successfully.');
      handleCloseEditWalkInModal();
    } catch (error) {
      alert('Failed to update. Please try again.');
    } finally {
      setEditWalkInLoading(false);
    }
  };

  // Handle Enter key press to move to next field
  const handleEnterKeyPress = (e, nextInputRef) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (nextInputRef && nextInputRef.current) {
        nextInputRef.current.focus();
      }
    }
  };

  const handleAddWalkInCustomer = async () => {
    const phoneDigits = (walkInCustomerPhone || '').replace(/\D/g, '');
    if (phoneDigits.length < 10) {
      alert('Please enter a valid 10-digit phone number');
      return;
    }
    if (!walkInCustomerName.trim()) {
      alert('Please enter customer name');
      return;
    }
    if (!walkInStaffId) {
      alert('Please assign staff who performed the service');
      return;
    }
    const combined = walkInCombinedServices.filter((s) => (s.name || '').trim());
    if (combined.length === 0) {
      alert('Please select at least one service or add a custom service');
      return;
    }
    const totalAmount = walkInEffectiveTotal;
    if (Number.isNaN(totalAmount) || totalAmount < 0) {
      alert('Please enter a valid total amount (0 or more)');
      return;
    }

    try {
      setWalkInLoading(true);

      // Get current time
      const now = new Date();
      const bookingTime = now.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
      });

      // Create today's date at midnight (matching mobile app format)
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const bookingDateISO = today.toISOString(); // "2026-01-09T00:00:00.000Z"

      const serviceNameDisplay = combined.map((s) => s.name.trim()).join(', ');
      const comboServices = combined.map((s) => ({ name: s.name.trim(), price: Number(s.price) || 0 }));
      const servicesSum = comboServices.reduce((sum, s) => sum + s.price, 0);
      const offerApplied = totalAmount < servicesSum ? totalAmount : undefined;

      // Create walk-in booking data (staff is mandatory)
      const walkInBookingData = {
        salonId: salonData.salonId,
        salonName: salonData.profile?.shopName || 'Ulloor Styles',
        salonAddress: salonData.profile?.address || '',
        salonLandmark: salonData.profile?.landmark || '',
        salonPhone: salonData.phoneNumber || '',
        customerId: 'walk-in-' + Date.now(),
        customerName: walkInCustomerName.trim(),
        customerPhone: walkInCustomerPhone.trim() || 'Not Provided',
        serviceName: serviceNameDisplay,
        servicePrice: comboServices[0]?.price ?? totalAmount,
        totalPrice: totalAmount,
        ...(offerApplied != null && { offerApplied }),
        serviceDuration: 30,
        bookingDate: bookingDateISO,
        bookingTime: bookingTime,
        bookingStatus: 'completed',
        serviceStatus: SERVICE_STATUS.COMPLETED,
        isWalkIn: true,
        isCombo: comboServices.length > 0,
        comboServices,
        staffId: walkInStaffId,
        paymentMode: walkInPaymentMode,
        createdAt: now,
        serviceCompletedAt: now,
        bookingReference: 'WLK-' + Date.now()
      };

      // Add to Firestore
      const walkInRef = await addDoc(collection(db, 'bookings'), walkInBookingData);
      
      // Generate bill for walk-in
      const walkInWithId = {
        ...walkInBookingData,
        id: walkInRef.id
      };
      await createBillFromBooking(walkInWithId, salonData, walkInPaymentMode);

      alert('Walk-in customer added and bill generated successfully!');
      handleCloseWalkInModal();
      // Real-time listener will auto-update the UI
    } catch (error) {
      alert('Failed to add walk-in customer. Please try again.');
    } finally {
      setWalkInLoading(false);
    }
  };

  // Staff Assignment Functions
  const handleOpenStaffModal = (booking) => {
    setSelectedBooking(booking);
    setShowStaffModal(true);
  };

  const handleCloseStaffModal = () => {
    setShowStaffModal(false);
    setSelectedBooking(null);
  };

  const handleAssignStaff = async (staffId) => {
    try {
      setStaffLoading(true);
      
      const bookingRef = doc(db, 'bookings', selectedBooking.id);
      await updateDoc(bookingRef, {
        staffId: staffId,
        updatedAt: new Date().toISOString()
      });

      alert('Staff assigned successfully!');
      handleCloseStaffModal();
      // Real-time listener will auto-update the UI
    } catch (error) {
      alert('Failed to assign staff. Please try again.');
    } finally {
      setStaffLoading(false);
    }
  };

  // Show loading if salon data is not yet loaded
  if (!salonData) {
    return (
      <div className="salon-dashboard">
        <div className="loading-container">
          <p>Loading salon information...</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="salon-dashboard">
        <div className="loading-container">
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // Render action button based on service status
  const renderActionButton = (booking) => {
    const serviceStatus = booking.serviceStatus || SERVICE_STATUS.NOT_STARTED;
    const isCancelled = booking.bookingStatus?.toLowerCase().includes('cancelled');
    const hasValidPhone = booking.customerPhone && 
                         booking.customerPhone !== 'Not Provided' && 
                         booking.customerPhone.trim().length > 0;

    // Don't show action buttons for cancelled bookings
    if (isCancelled) {
      return null;
    }

    if (serviceStatus === SERVICE_STATUS.COMPLETED) {
      // Show View Bill button and Send Thank Message button
      return (
        <>
          <button
            className="action-btn view-bill-btn"
            onClick={() => handleViewBill(booking)}
            disabled={actionLoading}
          >
            📄 View Bill
          </button>
          {hasValidPhone && !booking.thankYouMessageSent && (
            <button
              className="action-btn send-thanks-btn"
              onClick={() => handleSendThankYou(booking)}
              disabled={actionLoading}
            >
              💬 Send Thank Message
            </button>
          )}
        </>
      );
    } else if (serviceStatus === SERVICE_STATUS.IN_PROGRESS) {
      return (
        <button
          className="action-btn complete-btn"
          onClick={() => handleCompleteService(booking)}
          disabled={actionLoading}
        >
          ✅ Complete Service
        </button>
      );
    } else {
      return (
        <button
          className="action-btn start-btn"
          onClick={() => handleStartService(booking)}
          disabled={actionLoading}
        >
          ▶️ Start Service
        </button>
      );
    }
  };

  return (
    <div className="salon-dashboard">
      <div className="dashboard-header">
        <h2>Today's Schedule</h2>
        <div className="header-actions">
          <button onClick={handleOpenDailyReport} className="report-btn">
            📊 Get Today Report
          </button>
          <button onClick={handleOpenWalkInModal} className="walkin-btn">
            ➕ Add Walk-in Customer
          </button>
          <button onClick={fetchDashboardData} className="refresh-btn" disabled={loading} title="Data auto-updates in real-time">
            ✨ {loading ? 'Loading...' : 'Auto-Updating'}
          </button>
        </div>
      </div>

      <div className="info-banner">
        <span className="info-icon">ℹ️</span>
        <p>
          <strong>Staff Performance Tracking:</strong> Only bookings marked as <span className="highlight-completed">COMPLETED</span> count towards staff targets. Please update booking status after completing services.
        </p>
      </div>

      {/* Pending Action Banner - confirmed bookings whose slot time has elapsed today */}
      {pendingActionCount > 0 && (
        <div className="pending-action-banner">
          ⚠️ {pendingActionCount} pending action{pendingActionCount > 1 ? 's' : ''} — booked slot time has passed. Please start, complete, or cancel those bookings below.
        </div>
      )}

      {/* Today's Bookings */}
      <div className="bookings-section">
        <div className="section-header">
          <h3>Today's Bookings ({todayBookings.length})</h3>
        </div>

        {todayBookings.length === 0 ? (
          <div className="no-bookings">
            <p>No bookings scheduled for today</p>
          </div>
        ) : (
          <div className="bookings-grid">
            {todayBookings.map((booking) => {
              const isCancelled = booking.bookingStatus?.toLowerCase().includes('cancelled');
              const bookingStatus = booking.bookingStatus?.toLowerCase();
              const isInProgress = booking.serviceStatus === SERVICE_STATUS.IN_PROGRESS;
              const isCompleted = booking.serviceStatus === SERVICE_STATUS.COMPLETED;
              const needsStaffAssignment = isCompleted && !booking.staffId && !booking.isWalkIn;
              const isPendingAction = bookingStatus === 'confirmed' && !isInProgress && isBookingTimeElapsed(booking);
              const bookingCid = getBookingCustomerId(booking);
              const rewardApplied = booking.rewardApplied === true || booking.reward_applied === true;
              const isActiveRewardBooking = !rewardApplied && bookingCid && activeRewardBookingIdByCustomer[bookingCid] === booking.id;
              const rewardSummary = (bookingCid && rewardProgressByCustomer[bookingCid]?.rewardSummary) || '';
              
              // Determine who cancelled
              let cancelledBy = 'CANCELLED';
              if (bookingStatus === 'cancelled_by_salon') {
                cancelledBy = 'CANCELLED BY SALON';
              } else if (bookingStatus === 'cancelled_by_customer') {
                cancelledBy = 'CANCELLED BY CUSTOMER';
              }
              
              return (
                <div 
                  key={booking.id} 
                  className={`booking-card today-booking ${isCancelled ? 'cancelled-booking' : ''} ${isInProgress ? 'in-progress-highlight' : ''} ${isCompleted ? 'completed-booking' : ''} ${needsStaffAssignment ? 'pending-staff-assignment' : ''} ${isPendingAction ? 'pending-action-card' : ''}`}
                >
                  <div className="booking-card-badges">
                    <div className="booking-time-badge">
                      {booking.bookingTime}
                    </div>
                    {isCancelled && (
                      <span className="cancelled-label">{cancelledBy}</span>
                    )}
                    {isActiveRewardBooking && (
                      <div className="reward-applicable-badge">
                        <span className="reward-badge-icon">🎁</span>
                        Reward applicable this visit{rewardSummary ? `: ${rewardSummary}` : ''}
                      </div>
                    )}
                  </div>
                  
                  <div className="booking-info">
                    <div className="customer-name">
                      <span className="icon">👤</span>
                      <strong>{booking.customerName}</strong>
                    </div>
                    
                    <div className="booking-id">
                      <span className="icon">🔖</span>
                      <small>{booking.bookingReference || booking.id}</small>
                    </div>
                    
                    <div className="service-name">
                      <span className="icon">✂️</span>
                      {booking.serviceName}
                    </div>
                    
                    <div className="customer-contact">
                      <span className="icon">📞</span>
                      <span>{booking.customerPhone}</span>
                    </div>
                    
                    <BookingCardPriceBlock booking={booking} />
                    <AdvancePaymentBadge booking={booking} />
                    
                    {booking.serviceDuration && (
                      <div className="service-duration">
                        <span className="icon">⏱️</span>
                        <span>{booking.serviceDuration} min</span>
                      </div>
                    )}
                    
                    {booking.serviceStatus && !isCancelled && (
                      <div className={`service-status-badge status-${booking.serviceStatus}`}>
                        {booking.serviceStatus === SERVICE_STATUS.COMPLETED && '✅ '}
                        {booking.serviceStatus === SERVICE_STATUS.IN_PROGRESS && '⏳ '}
                        {booking.serviceStatus === SERVICE_STATUS.NOT_STARTED && '⏸️ '}
                        {booking.serviceStatus.replace('_', ' ').toUpperCase()}
                        {booking.serviceStatus === SERVICE_STATUS.COMPLETED}
                      </div>
                    )}
                    
                    {isCancelled && booking.cancellationReason && (
                      <div className="cancellation-reason">
                        <span className="icon">ℹ️</span>
                        <small>{booking.cancellationReason}</small>
                      </div>
                    )}

                    {isCompleted && !isCancelled && (
                      <div className="service-by-row">
                        <span className="service-by-label">Service by:</span>
                        <span className="service-by-name">{booking.staffId ? getStaffName(booking.staffId) : 'Not assigned'}</span>
                        <button
                          type="button"
                          className="service-by-edit-btn"
                          onClick={() => handleOpenStaffModal(booking)}
                          disabled={actionLoading}
                          title="Assign or change staff"
                          aria-label="Assign or change staff"
                        >
                          ✏️
                        </button>
                      </div>
                    )}
                  </div>

                  {!isCancelled && (
                    <div className="booking-actions">
                      {renderActionButton(booking)}

                      {/* Reschedule nudge — only while service hasn't started and the
                          slot time hasn't passed yet (mirrors mobile Today tab) */}
                      {(booking.serviceStatus === SERVICE_STATUS.NOT_STARTED || !booking.serviceStatus) &&
                        !isBookingTimeElapsed(booking) && (
                        <button
                          className={`action-btn reschedule-btn ${booking.rescheduleRequest?.status === 'pending' ? 'requested' : ''}`}
                          onClick={() => handleOpenRescheduleModal(booking)}
                          disabled={actionLoading || booking.rescheduleRequest?.status === 'pending'}
                        >
                          {booking.rescheduleRequest?.status === 'pending' ? '🔄 Requested' : '🔄 Reschedule'}
                        </button>
                      )}

                      {booking.serviceStatus !== SERVICE_STATUS.COMPLETED && (
                        <button
                          className="action-btn cancel-btn"
                          onClick={() => handleOpenCancelModal(booking)}
                          disabled={actionLoading}
                        >
                          ❌ Cancel
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Walk-in Customers */}
      <div className="bookings-section walkin-section">
        <div className="section-header">
          <h3>Walk-in Customers ({walkInBookings.length})</h3>
        </div>

        {walkInBookings.length === 0 ? (
          <div className="no-bookings">
            <p>No walk-in customers today</p>
          </div>
        ) : (
          <div className="bookings-grid">
            {walkInBookings.map((booking) => {
              const hasValidPhone = booking.customerPhone && 
                                   booking.customerPhone !== 'Not Provided' && 
                                   booking.customerPhone.trim().length > 0;
              
              return (
                <div key={booking.id} className="booking-card walkin-booking completed-booking">
                  <div className="booking-card-top walkin-card-top">
                    <div className="booking-time-badge walkin-badge">
                      {booking.bookingTime}
                    </div>
                    <div className="walkin-label-badge">WALK-IN</div>
                  </div>
                  <div className="booking-info">
                    <div className="customer-name">
                      <span className="icon">👤</span>
                      <strong>{booking.customerName}</strong>
                    </div>
                    
                    <div className="booking-id">
                      <span className="icon">🔖</span>
                      <small>{booking.bookingReference || booking.id}</small>
                    </div>
                    
                    <div className="service-name">
                      <span className="icon">✂️</span>
                      {booking.serviceName}
                    </div>
                    
                    <div className="customer-contact">
                      <span className="icon">📞</span>
                      <span>{booking.customerPhone}</span>
                    </div>
                    
                    <BookingCardPriceBlock booking={booking} />
                    <AdvancePaymentBadge booking={booking} />
                    
                    {booking.serviceDuration && (
                      <div className="service-duration">
                        <span className="icon">⏱️</span>
                        <span>{booking.serviceDuration} min</span>
                      </div>
                    )}
                    
                    <div className="service-status-badge status-completed">
                      ✅ COMPLETED
                    </div>

                    <div className="service-by-row">
                      <span className="service-by-label">Service by:</span>
                      <span className="service-by-name">{booking.staffId ? getStaffName(booking.staffId) : 'Not assigned'}</span>
                      <button
                        type="button"
                        className="service-by-edit-btn"
                        onClick={() => handleOpenStaffModal(booking)}
                        disabled={actionLoading}
                        title="Assign or change staff"
                        aria-label="Assign or change staff"
                      >
                        ✏️
                      </button>
                    </div>
                  </div>

                  <div className="booking-actions">
                    <button
                      className="action-btn view-bill-btn"
                      onClick={() => handleViewBill(booking)}
                      disabled={actionLoading}
                    >
                      📄 View Bill
                    </button>
                    <button
                      className="action-btn edit-walkin-btn"
                      onClick={() => handleOpenEditWalkInModal(booking)}
                      disabled={actionLoading}
                    >
                      ✏️ Edit
                    </button>
                    {hasValidPhone && !booking.thankYouMessageSent && (
                      <button
                        className="action-btn send-thanks-btn"
                        onClick={() => handleSendThankYou(booking)}
                        disabled={actionLoading}
                      >
                        💬 Send Thank Message
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Upcoming Bookings */}
      <div className="bookings-section upcoming-section">
        <div className="section-header">
          <h3>Upcoming Bookings ({upcomingBookings.length})</h3>
        </div>

        {upcomingBookings.length === 0 ? (
          <div className="no-bookings">
            <p>No upcoming bookings</p>
          </div>
        ) : (
          <div className="bookings-grid">
            {upcomingBookings.map((booking) => {
              const bookingCid = getBookingCustomerId(booking);
              const rewardApplied = booking.rewardApplied === true || booking.reward_applied === true;
              const isActiveRewardBooking = !rewardApplied && bookingCid && activeRewardBookingIdByCustomer[bookingCid] === booking.id;
              const rewardSummary = (bookingCid && rewardProgressByCustomer[bookingCid]?.rewardSummary) || '';
              return (
              <div key={booking.id} className="booking-card upcoming-booking">
                <div className="booking-card-badges">
                  <div className="booking-date-badge">
                    {formatDate(booking.bookingDate)}
                  </div>
                  {isActiveRewardBooking && (
                    <div className="reward-applicable-badge">
                      <span className="reward-badge-icon">🎁</span>
                      Reward applicable this visit{rewardSummary ? `: ${rewardSummary}` : ''}
                    </div>
                  )}
                </div>
                
                <div className="booking-info">
                  <div className="customer-name">
                    <span className="icon">👤</span>
                    <strong>{booking.customerName}</strong>
                  </div>
                  
                  <div className="booking-id">
                    <span className="icon">🔖</span>
                    <small>{booking.bookingReference || booking.id}</small>
                  </div>
                  
                  <div className="service-name">
                    <span className="icon">✂️</span>
                    {booking.serviceName}
                  </div>
                  
                  <div className="booking-time">
                    <span className="icon">⏰</span>
                    {booking.bookingTime}
                  </div>
                  
                  <div className="customer-contact">
                    <span className="icon">📞</span>
                    {booking.customerPhone}
                  </div>
                  
                  <BookingCardPriceBlock booking={booking} />
                    <AdvancePaymentBadge booking={booking} />
                  
                  {booking.serviceDuration && (
                    <div className="service-duration">
                      <span className="icon">⏱️</span>
                      <span>{booking.serviceDuration} min</span>
                    </div>
                  )}
                </div>

                <div className="booking-actions">
                  <button
                    className={`action-btn reschedule-btn ${booking.rescheduleRequest?.status === 'pending' ? 'requested' : ''}`}
                    onClick={() => handleOpenRescheduleModal(booking)}
                    disabled={actionLoading || booking.rescheduleRequest?.status === 'pending'}
                  >
                    {booking.rescheduleRequest?.status === 'pending' ? '🔄 Reschedule Requested' : '🔄 Reschedule'}
                  </button>
                  <button
                    className="action-btn cancel-btn"
                    onClick={() => handleOpenCancelModal(booking)}
                    disabled={actionLoading}
                  >
                    ❌ Cancel Booking
                  </button>
                </div>
              </div>
            );
            })}
          </div>
        )}
      </div>

      {/* Cancel Booking Modal */}
      {showCancelModal && (
        <div className="modal-overlay" onClick={handleCloseCancelModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Cancel Booking</h3>
              <button className="modal-close" onClick={handleCloseCancelModal}>✕</button>
            </div>
            
            <div className="modal-body">
              <p className="booking-details-modal">
                <strong>Customer:</strong> {selectedBooking?.customerName}<br />
                <strong>Service:</strong> {selectedBooking?.serviceName}<br />
                <strong>Time:</strong> {selectedBooking?.bookingTime}
              </p>

              <label className="modal-label">Select Cancellation Reason:</label>
              <select 
                value={cancelReason} 
                onChange={(e) => setCancelReason(e.target.value)}
                className="cancel-reason-select"
              >
                <option value="">-- Select Reason --</option>
                <option value="Customer requested cancellation">Customer requested cancellation</option>
                <option value="Staff unavailable">Staff unavailable</option>
                <option value="Emergency closure">Emergency closure</option>
                <option value="Customer did not show up">Customer did not show up</option>
                <option value="Double booking">Double booking</option>
                <option value="Other">Other</option>
              </select>

              {cancelReason === 'Other' && (
                <textarea
                  placeholder="Enter reason..."
                  value={cancelReason === 'Other' ? '' : cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  className="cancel-reason-textarea"
                />
              )}
            </div>

            <div className="modal-footer">
              <button 
                className="modal-btn cancel-confirm-btn" 
                onClick={handleCancelBooking}
                disabled={!cancelReason || actionLoading}
              >
                {actionLoading ? 'Cancelling...' : 'Confirm Cancel'}
              </button>
              <button
                className="modal-btn modal-close-btn"
                onClick={handleCloseCancelModal}
                disabled={actionLoading}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reschedule Request Modal — mirrors the mobile owner dashboard */}
      {showRescheduleModal && bookingForReschedule && (
        <div className="modal-overlay" onClick={handleCloseRescheduleModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Ask Customer to Reschedule</h3>
              <button className="modal-close" onClick={handleCloseRescheduleModal}>✕</button>
            </div>

            <div className="modal-body">
              <p className="booking-details-modal">
                <strong>Customer:</strong> {bookingForReschedule.customerName}<br />
                <strong>Service:</strong> {bookingForReschedule.serviceName}<br />
                <strong>Time:</strong> {bookingForReschedule.bookingTime}
              </p>

              <p className="reschedule-message-preview">
                The customer will see: “{RESCHEDULE_REQUEST_MESSAGE}”. Their slot stays
                held until they pick a new time or the booking is cancelled.
              </p>

              <label className="modal-label">Preferred time window:</label>
              <div className="reschedule-options">
                {Object.entries(RESCHEDULE_PREFERRED_TIME_LABELS).map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    className={`reschedule-option-btn ${selectedPreferredTime === key ? 'selected' : ''}`}
                    onClick={() => setSelectedPreferredTime(key)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="modal-btn reschedule-send-btn"
                onClick={handleConfirmRescheduleRequest}
                disabled={!selectedPreferredTime || actionLoading}
              >
                {actionLoading ? 'Sending...' : 'Send Request'}
              </button>
              <button
                className="modal-btn modal-close-btn"
                onClick={handleCloseRescheduleModal}
                disabled={actionLoading}
              >
                Never Mind
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Mode Modal */}
      {showPaymentModeModal && (
        <div className="modal-overlay" onClick={handleClosePaymentModeModal}>
          <div className="modal-content payment-mode-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Complete Service - Select Payment Mode</h3>
              <button className="modal-close" onClick={handleClosePaymentModeModal}>✕</button>
            </div>
            
            <div className="modal-body">
              {bookingForCompletion && (
                <div className="booking-details-modal">
                  <p><strong>Customer:</strong> {bookingForCompletion.customerName}</p>
                  <p><strong>Service:</strong> {bookingForCompletion.serviceName}</p>
                  <p><strong>Amount:</strong> ₹{bookingForCompletion.totalPrice || bookingForCompletion.servicePrice}</p>
                  {bookingForCompletion.advancePaid === true && (
                    <p className="advance-collect-note">
                      💳 Advance ₹{bookingForCompletion.advanceAmount || 11} already paid online —
                      collect <strong>₹{Math.max(Number(bookingForCompletion.remainingAmount) || 0, 0)}</strong> from the customer
                    </p>
                  )}
                </div>
              )}

              <div className="payment-mode-selection">
                <label className="modal-label">How did the customer pay? *</label>
                <div className="payment-mode-options-large">
                  <button
                    type="button"
                    className={`payment-mode-btn-large ${selectedPaymentMode === 'cash' ? 'active' : ''}`}
                    onClick={() => setSelectedPaymentMode('cash')}
                  >
                    <span className="payment-icon">💵</span>
                    <span className="payment-label">Cash</span>
                  </button>
                  <button
                    type="button"
                    className={`payment-mode-btn-large ${selectedPaymentMode === 'upi' ? 'active' : ''}`}
                    onClick={() => setSelectedPaymentMode('upi')}
                  >
                    <span className="payment-icon">📱</span>
                    <span className="payment-label">UPI</span>
                  </button>
                  <button
                    type="button"
                    className={`payment-mode-btn-large ${selectedPaymentMode === 'card' ? 'active' : ''}`}
                    onClick={() => setSelectedPaymentMode('card')}
                  >
                    <span className="payment-icon">💳</span>
                    <span className="payment-label">Card</span>
                  </button>
                </div>
              </div>

              <div className="completion-staff-selection">
                <label className="modal-label">Assign Staff *</label>
                {bookingForCompletion?.staffId && !completionStaffId ? (
                  <p className="completion-current-staff">
                    Currently assigned: {getStaffName(bookingForCompletion.staffId)}
                  </p>
                ) : null}
                <div className="completion-staff-list">
                  {staff.length === 0 ? (
                    <div className="completion-no-staff-block">
                      <p className="completion-no-staff">No staff added yet. Add a staff member to complete this service.</p>
                      <button
                        type="button"
                        className="modal-btn add-staff-redirect-btn"
                        onClick={() => {
                          handleClosePaymentModeModal();
                          navigate('/salon/staff-management');
                        }}
                      >
                        ➕ Add Staff
                      </button>
                    </div>
                  ) : (
                    staff.map((staffMember) => (
                      <button
                        key={staffMember.id}
                        type="button"
                        className={`completion-staff-btn ${completionStaffId === staffMember.id ? 'active' : ''}`}
                        onClick={() => setCompletionStaffId(completionStaffId === staffMember.id ? null : staffMember.id)}
                      >
                        {staffMember.name}
                        {completionStaffId === staffMember.id ? ' ✓' : ''}
                      </button>
                    ))
                  )}
                </div>
                {completionStaffId ? (
                  <button
                    type="button"
                    className="completion-staff-clear"
                    onClick={() => setCompletionStaffId(null)}
                  >
                    Clear selection
                  </button>
                ) : null}
              </div>
            </div>

            <div className="modal-footer">
              <button 
                className="modal-btn complete-confirm-btn" 
                onClick={handleConfirmCompletion}
                disabled={!selectedPaymentMode || !completionStaffId || actionLoading}
              >
                {actionLoading ? 'Processing...' : '✅ Complete & Generate Bill'}
              </button>
              <button 
                className="modal-btn modal-close-btn" 
                onClick={handleClosePaymentModeModal}
                disabled={actionLoading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Walk-in Customer Modal */}
      {showWalkInModal && (
        <div className="modal-overlay" onClick={handleCloseWalkInModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add Walk-in Customer</h3>
              <button className="modal-close" onClick={handleCloseWalkInModal}>✕</button>
            </div>
            
            <div className="modal-body">
              <div className="form-group phone-search-group">
                <label className="modal-label">Phone Number *</label>
                <input
                  ref={phoneInputRef}
                  type="tel"
                  value={walkInCustomerPhone}
                  onChange={(e) => handleWalkInPhoneChange(e.target.value)}
                  onKeyDown={(e) => handleEnterKeyPress(e, nameInputRef)}
                  placeholder="Enter 10-digit phone number"
                  maxLength="10"
                  className="modal-input"
                  autoFocus
                />
                {phoneSearchLoading && <span className="phone-search-loading">Searching...</span>}
                {phoneSearchResults.length > 0 && (
                  <ul ref={phoneListRef} className="phone-search-list" role="listbox">
                    {phoneSearchResults.map((item, idx) => (
                      <li
                        key={`${item.phone}-${idx}`}
                        role="option"
                        aria-selected="false"
                        className="phone-search-item"
                        onClick={() => handleSelectPhoneMatch(item)}
                      >
                        <span className="phone-search-phone">{item.phone}</span>
                        <span className="phone-search-name">{item.name}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="form-group">
                <label className="modal-label">Customer Name *</label>
                <input
                  ref={nameInputRef}
                  type="text"
                  value={walkInCustomerName}
                  onChange={(e) => setWalkInCustomerName(e.target.value)}
                  onKeyDown={(e) => handleEnterKeyPress(e, null)}
                  placeholder="Enter customer name"
                  className="modal-input"
                />
              </div>

              <div className="form-group">
                <label className="modal-label">Assign Staff *</label>
                {staff.length === 0 ? (
                  <div className="walkin-no-staff">
                    <p className="walkin-no-staff-message">No staff added yet. Add a staff member to record walk-in services.</p>
                    <button
                      type="button"
                      className="modal-btn add-staff-redirect-btn"
                      onClick={() => {
                        handleCloseWalkInModal();
                        navigate('/salon/staff-management');
                      }}
                    >
                      ➕ Add Staff
                    </button>
                  </div>
                ) : (
                  <select
                    value={walkInStaffId}
                    onChange={(e) => setWalkInStaffId(e.target.value)}
                    className="modal-input"
                  >
                    <option value="">— Select staff —</option>
                    {staff.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                )}
              </div>

              <div className="form-group">
                <label className="modal-label">Services * (select multiple)</label>
                {walkInServiceList.length > 0 && (
                  <div className="walkin-service-checklist">
                    {walkInServiceList.map((s) => {
                      const key = `${s.name}-${s.price}`;
                      const isChecked = walkInSelectedFromList.some((x) => `${x.name}-${x.price}` === key);
                      return (
                        <label key={key} className="walkin-service-checkbox-label">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => toggleWalkInServiceFromList(s)}
                            className="walkin-service-checkbox"
                          />
                          <span>{s.label}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
                <div className="walkin-custom-entries">
                  <span className="modal-label" style={{ display: 'block', marginBottom: '0.25rem' }}>Other (enter manually)</span>
                  {walkInCustomEntries.map((entry, index) => (
                    <div key={index} className="walkin-custom-service-fields walkin-custom-row">
                      <input
                        type="text"
                        value={entry.name}
                        onChange={(e) => updateWalkInCustomEntry(index, 'name', e.target.value)}
                        placeholder="Service name"
                        className="modal-input"
                      />
                      <input
                        type="number"
                        value={entry.price || ''}
                        onChange={(e) => updateWalkInCustomEntry(index, 'price', e.target.value)}
                        placeholder="₹"
                        min="0"
                        step="1"
                        className="modal-input walkin-amount-input"
                      />
                      <button
                        type="button"
                        className="walkin-remove-custom-btn"
                        onClick={() => removeWalkInCustomEntry(index)}
                        aria-label="Remove service"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  <button type="button" className="modal-btn walkin-add-custom-btn" onClick={addWalkInCustomEntry}>
                    + Add custom service
                  </button>
                </div>
                {(walkInCombinedServices.length > 0) && (
                  <div className="walkin-total-amount-group">
                    <label className="modal-label">Total amount (₹) *</label>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={walkInEffectiveTotal}
                      onChange={(e) => {
                        const v = e.target.value.trim();
                        if (v === '') {
                          setWalkInTotalOverride(null);
                          return;
                        }
                        const n = parseFloat(v);
                        if (!Number.isNaN(n) && n >= 0) setWalkInTotalOverride(n);
                      }}
                      placeholder={String(walkInTotalAmount)}
                      className="modal-input walkin-total-input"
                    />
                  </div>
                )}
              </div>

              <div className="form-group">
                <label className="modal-label">Payment Mode *</label>
                <div className="payment-mode-options">
                  <button
                    type="button"
                    className={`payment-mode-btn ${walkInPaymentMode === 'cash' ? 'active' : ''}`}
                    onClick={() => setWalkInPaymentMode('cash')}
                  >
                    💵 Cash
                  </button>
                  <button
                    type="button"
                    className={`payment-mode-btn ${walkInPaymentMode === 'upi' ? 'active' : ''}`}
                    onClick={() => setWalkInPaymentMode('upi')}
                  >
                    📱 UPI
                  </button>
                  <button
                    type="button"
                    className={`payment-mode-btn ${walkInPaymentMode === 'card' ? 'active' : ''}`}
                    onClick={() => setWalkInPaymentMode('card')}
                  >
                    💳 Card
                  </button>
                </div>
              </div>

              <p className="modal-note">
                * This will be recorded as a completed booking for today and bill will be generated automatically.
              </p>
            </div>

            <div className="modal-footer">
              <button 
                className="modal-btn add-walkin-btn" 
                onClick={handleAddWalkInCustomer}
                disabled={walkInLoading || !walkInStaffId}
              >
                {walkInLoading ? 'Adding...' : 'Add Customer'}
              </button>
              <button 
                className="modal-btn modal-close-btn" 
                onClick={handleCloseWalkInModal}
                disabled={walkInLoading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Walk-in Modal */}
      {showEditWalkInModal && editingWalkIn && (
        <div className="modal-overlay" onClick={handleCloseEditWalkInModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Walk-in</h3>
              <button className="modal-close" onClick={handleCloseEditWalkInModal}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="modal-label">Customer Name *</label>
                <input
                  type="text"
                  value={editWalkInName}
                  onChange={(e) => setEditWalkInName(e.target.value)}
                  placeholder="Enter customer name"
                  className="modal-input"
                />
              </div>
              <div className="form-group">
                <label className="modal-label">Phone Number *</label>
                <input
                  type="tel"
                  value={editWalkInPhone}
                  onChange={(e) => setEditWalkInPhone(e.target.value.replace(/\D/g, ''))}
                  placeholder="Enter 10-digit phone number"
                  maxLength="10"
                  className="modal-input"
                />
              </div>
              <div className="form-group">
                <label className="modal-label">Services * (select multiple)</label>
                {walkInServiceList.length > 0 && (
                  <div className="walkin-service-checklist">
                    {walkInServiceList.map((s) => {
                      const key = `${s.name}-${s.price}`;
                      const isChecked = editWalkInSelectedFromList.some((x) => `${x.name}-${x.price}` === key);
                      return (
                        <label key={key} className="walkin-service-checkbox-label">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => toggleEditWalkInServiceFromList(s)}
                            className="walkin-service-checkbox"
                          />
                          <span>{s.label}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
                <div className="walkin-custom-entries">
                  <span className="modal-label" style={{ display: 'block', marginBottom: '0.25rem' }}>Other (enter manually)</span>
                  {editWalkInCustomEntries.map((entry, index) => (
                    <div key={index} className="walkin-custom-service-fields walkin-custom-row">
                      <input
                        type="text"
                        value={entry.name}
                        onChange={(e) => updateEditWalkInCustomEntry(index, 'name', e.target.value)}
                        placeholder="Service name"
                        className="modal-input"
                      />
                      <input
                        type="number"
                        value={entry.price || ''}
                        onChange={(e) => updateEditWalkInCustomEntry(index, 'price', e.target.value)}
                        placeholder="₹"
                        min="0"
                        step="1"
                        className="modal-input walkin-amount-input"
                      />
                      <button
                        type="button"
                        className="walkin-remove-custom-btn"
                        onClick={() => removeEditWalkInCustomEntry(index)}
                        aria-label="Remove service"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  <button type="button" className="modal-btn walkin-add-custom-btn" onClick={addEditWalkInCustomEntry}>
                    + Add custom service
                  </button>
                </div>
                {(editWalkInCombinedServices.length > 0) && (
                  <div className="walkin-total-amount-group">
                    <label className="modal-label">Total amount (₹) *</label>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={editWalkInEffectiveTotal}
                      onChange={(e) => {
                        const v = e.target.value.trim();
                        if (v === '') {
                          setEditWalkInTotalOverride(null);
                          return;
                        }
                        const n = parseFloat(v);
                        if (!Number.isNaN(n) && n >= 0) setEditWalkInTotalOverride(n);
                      }}
                      placeholder={String(editWalkInTotalAmount)}
                      className="modal-input walkin-total-input"
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="modal-btn add-walkin-btn" onClick={handleSaveEditWalkIn} disabled={editWalkInLoading}>
                {editWalkInLoading ? 'Saving...' : 'Save'}
              </button>
              <button className="modal-btn modal-close-btn" onClick={handleCloseEditWalkInModal} disabled={editWalkInLoading}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Staff Assignment Modal */}
      {showStaffModal && (
        <div className="modal-overlay" onClick={handleCloseStaffModal}>
          <div className="modal-container staff-assignment-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Assign Staff</h2>
              <button className="modal-close-btn" onClick={handleCloseStaffModal}>✕</button>
            </div>

            <div className="modal-body">
              {selectedBooking && (
                <div className="booking-info-modal">
                  <p><strong>Customer:</strong> {selectedBooking.customerName}</p>
                  <p><strong>Service:</strong> {selectedBooking.serviceName}</p>
                  <p><strong>Time:</strong> {selectedBooking.bookingTime}</p>
                  {selectedBooking.staffId && (
                    <p className="current-staff-info">
                      <strong>Currently Assigned:</strong> {getStaffName(selectedBooking.staffId)}
                    </p>
                  )}
                </div>
              )}

              <div className="staff-list-modal">
                {staffLoading ? (
                  <p className="loading-text">Loading staff...</p>
                ) : staff.length === 0 ? (
                  <div className="no-staff-message">
                    <p>No active staff members available.</p>
                    <p className="hint-text">Please add staff members in Staff Management.</p>
                  </div>
                ) : (
                  staff.map((staffMember) => (
                    <div 
                      key={staffMember.id} 
                      className="staff-option"
                      onClick={() => handleAssignStaff(staffMember.id)}
                    >
                      <div className="staff-option-info">
                        <h4>{staffMember.name}</h4>
                        {staffMember.phoneNumber && (
                          <p className="staff-phone">{staffMember.phoneNumber}</p>
                        )}
                      </div>
                      <button className="assign-btn">
                        {selectedBooking?.staffId === staffMember.id ? 'Reassign' : 'Assign'}
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button 
                className="modal-btn modal-close-btn" 
                onClick={handleCloseStaffModal}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bill View Modal */}
      {showBillView && selectedBookingForBill && (
        <BillView 
          bookingId={selectedBookingForBill} 
          onClose={handleCloseBillView} 
        />
      )}

      {/* Daily Report Modal */}
      {showDailyReport && (
        <DailyReport 
          salonData={salonData} 
          onClose={handleCloseDailyReport} 
        />
      )}
    </div>
  );
}

export default Dashboard;

